// frontend/src/profile/UserProfile.js
// ═══════════════════════════════════════════════════════════════════════════
// MI PERFIL - UrbanQ
// Diseno basado en AdminContabilidad.js y AdminSupport.js
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiUser, FiMail, FiPhone, FiMapPin, FiCalendar,
    FiLock, FiBell, FiShield, FiEdit2, FiCheck,
    FiX, FiAlertCircle, FiEye, FiEyeOff, FiRefreshCw,
    FiCreditCard, FiFileText, FiActivity, FiCheckCircle,
    FiLogOut, FiHome, FiClock
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../config/api';
import './UserProfile.css';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const UserProfile = ({ user, onLogout, onUserUpdate }) => {
    // Estados
    const [activeTab, setActiveTab] = useState('informacion');
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);

    // Estados para editar perfil
    const [showEditModal, setShowEditModal] = useState(false);
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');

    // Estados para cambiar contrasena
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    // Estados para notificaciones
    const [notifPayments, setNotifPayments] = useState(true);
    const [notifNotices, setNotifNotices] = useState(true);
    const [notifEmail, setNotifEmail] = useState(true);

    // Estadisticas
    const [stats, setStats] = useState({
        totalPayments: 0,
        pendingPayments: 0,
        totalNotices: 0
    });

    const token = localStorage.getItem('token');

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES AUXILIARES
    // ═══════════════════════════════════════════════════════════════════════

    const showNotificationMsg = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const words = name.trim().split(' ');
        if (words.length === 1) return words[0].charAt(0).toUpperCase();
        return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-EC', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        } catch (e) {
            return 'N/A';
        }
    };

    // Calcular fortaleza de contrasena
    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 6) strength += 1;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        return strength;
    };

    // ═══════════════════════════════════════════════════════════════════════
    // CARGAR ESTADISTICAS
    // ═══════════════════════════════════════════════════════════════════════

    const loadUserStats = useCallback(async () => {
        try {
            // Cargar pagos
            const paymentsRes = await fetch(API_ENDPOINTS.payments, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const paymentsData = await paymentsRes.json();

            if (paymentsData.success) {
                const payments = paymentsData.data || [];
                setStats(prev => ({
                    ...prev,
                    totalPayments: payments.filter(p => p.status === 'PAGADO').length,
                    pendingPayments: payments.filter(p => p.status === 'PENDIENTE').length
                }));
            }

            // Cargar avisos
            const noticesRes = await fetch(API_ENDPOINTS.notices, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const noticesData = await noticesRes.json();

            if (noticesData.success) {
                setStats(prev => ({
                    ...prev,
                    totalNotices: (noticesData.data || []).length
                }));
            }
        } catch (err) {
            console.error('Error cargando estadisticas:', err);
        }
    }, [token]);

    useEffect(() => {
        loadUserStats();
    }, [loadUserStats]);

    useEffect(() => {
        if (user) {
            setEditName(user.name || '');
            setEditPhone(user.phone || '');
        }
    }, [user]);

    useEffect(() => {
        setPasswordStrength(calculatePasswordStrength(newPassword));
    }, [newPassword]);

    // ═══════════════════════════════════════════════════════════════════════
    // CAMBIAR CONTRASENA
    // ═══════════════════════════════════════════════════════════════════════

    const handleChangePassword = async () => {
        if (!currentPassword) {
            showNotificationMsg('Ingresa tu contrasena actual', 'error');
            return;
        }
        if (!newPassword) {
            showNotificationMsg('Ingresa la nueva contrasena', 'error');
            return;
        }
        if (newPassword.length < 6) {
            showNotificationMsg('La nueva contrasena debe tener al menos 6 caracteres', 'error');
            return;
        }
        if (newPassword !== confirmPassword) {
            showNotificationMsg('Las contrasenas no coinciden', 'error');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(API_ENDPOINTS.changePassword || `${API_ENDPOINTS.auth}/change-password`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    currentPassword,
                    newPassword
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al cambiar la contrasena');
            }

            showNotificationMsg('Contrasena actualizada correctamente', 'success');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            showNotificationMsg(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // ACTUALIZAR PERFIL
    // ═══════════════════════════════════════════════════════════════════════

    const handleUpdateProfile = async () => {
        if (!editName.trim()) {
            showNotificationMsg('El nombre es requerido', 'error');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(API_ENDPOINTS.updateProfile || `${API_ENDPOINTS.auth}/profile`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: editName.trim(),
                    phone: editPhone.trim()
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al actualizar el perfil');
            }

            showNotificationMsg('Perfil actualizado correctamente', 'success');
            setShowEditModal(false);

            if (onUserUpdate) {
                onUserUpdate({ ...user, name: editName.trim(), phone: editPhone.trim() });
            }
        } catch (err) {
            showNotificationMsg(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDERIZADO
    // ═══════════════════════════════════════════════════════════════════════

    return (
        <div className="urbanq-profile-container">
            {/* Notificacion */}
            {notification && (
                <div className={`urbanq-profile-notification ${notification.type}`}>
                    {notification.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)}><FiX /></button>
                </div>
            )}

            {/* Tabs — Tabs uniformes */}
            <div className="urbanq-user-tabs urbanq-profile-tabs-wrapper">
                <button
                    className={`urbanq-user-tab ${activeTab === 'informacion' ? 'active' : ''}`}
                    onClick={() => setActiveTab('informacion')}
                >
                    <FiUser /> Informacion
                </button>
                <button
                    className={`urbanq-user-tab ${activeTab === 'seguridad' ? 'active' : ''}`}
                    onClick={() => setActiveTab('seguridad')}
                >
                    <FiLock /> Seguridad
                </button>
                <button
                    className={`urbanq-user-tab ${activeTab === 'notificaciones' ? 'active' : ''}`}
                    onClick={() => setActiveTab('notificaciones')}
                >
                    <FiBell /> Notificaciones
                </button>
            </div>

            {/* Contenido */}
            <div className="urbanq-profile-content">
                <div className="urbanq-profile-layout">
                    {/* Columna Izquierda - Card de Perfil */}
                    <div className="urbanq-profile-sidebar">
                        <div className="urbanq-profile-card-main">
                            <div className="urbanq-profile-avatar-wrapper">
                                <div className="urbanq-profile-avatar">
                                    <span>{getInitials(user?.name)}</span>
                                </div>
                                <div className="urbanq-profile-status online"></div>
                            </div>
                            <h3 className="urbanq-profile-name">{user?.name || 'Usuario'}</h3>
                            <p className="urbanq-profile-username">@{user?.username || 'username'}</p>
                            <div className="urbanq-profile-role-badge">
                                {user?.role === 'Admin' ? 'Administrador' : 'Residente'}
                            </div>

                            {/* Mini Stats */}
                            <div className="urbanq-profile-mini-stats">
                                <div className="mini-stat">
                                    <div className="mini-stat-icon green">
                                        <FiCreditCard />
                                    </div>
                                    <div className="mini-stat-info">
                                        <span className="mini-stat-value">{stats.totalPayments}</span>
                                        <span className="mini-stat-label">Pagos</span>
                                    </div>
                                </div>
                                <div className="mini-stat">
                                    <div className="mini-stat-icon orange">
                                        <FiClock />
                                    </div>
                                    <div className="mini-stat-info">
                                        <span className="mini-stat-value">{stats.pendingPayments}</span>
                                        <span className="mini-stat-label">Pendientes</span>
                                    </div>
                                </div>
                                <div className="mini-stat">
                                    <div className="mini-stat-icon blue">
                                        <FiBell />
                                    </div>
                                    <div className="mini-stat-info">
                                        <span className="mini-stat-value">{stats.totalNotices}</span>
                                        <span className="mini-stat-label">Avisos</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Boton Cerrar Sesion */}
                        <button className="urbanq-profile-logout-btn" onClick={onLogout}>
                            <FiLogOut /> Cerrar Sesion
                        </button>
                    </div>

                    {/* Columna Derecha - Contenido del Tab */}
                    <div className="urbanq-profile-main">
                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* TAB: INFORMACION */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        {activeTab === 'informacion' && (
                            <div className="urbanq-profile-tab-content">
                                <div className="urbanq-profile-section">
                                    <div className="urbanq-profile-section-header">
                                        <h2><FiUser /> Informacion Personal</h2>
                                        <button
                                            className="urbanq-profile-btn secondary"
                                            onClick={() => setShowEditModal(true)}
                                        >
                                            <FiEdit2 /> Editar
                                        </button>
                                    </div>

                                    <div className="urbanq-profile-info-grid">
                                        <InfoRow
                                            icon={<FiUser />}
                                            label="Nombre Completo"
                                            value={user?.name || 'No especificado'}
                                        />
                                        <InfoRow
                                            icon={<FiMail />}
                                            label="Correo Electronico"
                                            value={user?.email || 'No especificado'}
                                        />
                                        <InfoRow
                                            icon={<FiPhone />}
                                            label="Telefono"
                                            value={user?.phone || 'No especificado'}
                                        />
                                        <InfoRow
                                            icon={<FiHome />}
                                            label="Apartamento / Unidad"
                                            value={user?.apartment || 'No especificado'}
                                        />
                                        <InfoRow
                                            icon={<FiMapPin />}
                                            label="Condominio"
                                            value={user?.condominium_name || 'UrbanQ'}
                                        />
                                        <InfoRow
                                            icon={<FiCalendar />}
                                            label="Miembro desde"
                                            value={formatDate(user?.created_at)}
                                        />
                                    </div>
                                </div>

                                {/* Resumen de Actividad */}
                                <div className="urbanq-profile-section">
                                    <h2><FiActivity /> Resumen de Actividad</h2>
                                    <div className="urbanq-profile-activity-grid">
                                        <ActivityCard
                                            icon={<FiCheckCircle />}
                                            label="Pagos Completados"
                                            value={stats.totalPayments}
                                            color="green"
                                        />
                                        <ActivityCard
                                            icon={<FiClock />}
                                            label="Pagos Pendientes"
                                            value={stats.pendingPayments}
                                            color="orange"
                                        />
                                        <ActivityCard
                                            icon={<FiFileText />}
                                            label="Avisos Recibidos"
                                            value={stats.totalNotices}
                                            color="blue"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* TAB: SEGURIDAD */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        {activeTab === 'seguridad' && (
                            <div className="urbanq-profile-tab-content">
                                <div className="urbanq-profile-section">
                                    <h2><FiLock /> Cambiar Contrasena</h2>
                                    <p className="urbanq-profile-section-desc">
                                        Actualiza tu contrasena regularmente para mantener tu cuenta segura
                                    </p>

                                    <div className="urbanq-profile-password-form">
                                        <div className="urbanq-profile-form-group">
                                            <label>Contrasena Actual</label>
                                            <div className="urbanq-profile-input-wrapper">
                                                <input
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    placeholder="Ingresa tu contrasena actual"
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    disabled={loading}
                                                />
                                                <button
                                                    type="button"
                                                    className="urbanq-profile-toggle-password"
                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                >
                                                    {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="urbanq-profile-form-group">
                                            <label>Nueva Contrasena</label>
                                            <div className="urbanq-profile-input-wrapper">
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    placeholder="Minimo 6 caracteres"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    disabled={loading}
                                                />
                                                <button
                                                    type="button"
                                                    className="urbanq-profile-toggle-password"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                >
                                                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                                                </button>
                                            </div>
                                            {/* Indicador de fortaleza */}
                                            {newPassword && (
                                                <div className="urbanq-profile-password-strength">
                                                    <div className="strength-bars">
                                                        {[1, 2, 3, 4, 5].map(level => (
                                                            <div
                                                                key={level}
                                                                className={`strength-bar ${passwordStrength >= level ? `level-${passwordStrength}` : ''}`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className={`strength-text level-${passwordStrength}`}>
                                                        {passwordStrength <= 1 && 'Muy debil'}
                                                        {passwordStrength === 2 && 'Debil'}
                                                        {passwordStrength === 3 && 'Regular'}
                                                        {passwordStrength === 4 && 'Fuerte'}
                                                        {passwordStrength === 5 && 'Muy fuerte'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="urbanq-profile-form-group">
                                            <label>Confirmar Nueva Contrasena</label>
                                            <input
                                                type="password"
                                                placeholder="Repite la nueva contrasena"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                disabled={loading}
                                            />
                                            {confirmPassword && newPassword !== confirmPassword && (
                                                <span className="urbanq-profile-error-text">
                                                    Las contrasenas no coinciden
                                                </span>
                                            )}
                                            {confirmPassword && newPassword === confirmPassword && confirmPassword.length >= 6 && (
                                                <span className="urbanq-profile-success-text">
                                                    <FiCheck /> Las contrasenas coinciden
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            className="urbanq-profile-btn primary"
                                            onClick={handleChangePassword}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <><FiRefreshCw className="spinning" /> Actualizando...</>
                                            ) : (
                                                <><FiLock /> Actualizar Contrasena</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Informacion de seguridad */}
                                <div className="urbanq-profile-section">
                                    <h2><FiShield /> Estado de Seguridad</h2>
                                    <div className="urbanq-profile-security-status">
                                        <div className="security-status-item">
                                            <FiCheck className="status-icon success" />
                                            <div>
                                                <strong>Cuenta activa</strong>
                                                <p>Tu cuenta esta verificada y activa</p>
                                            </div>
                                        </div>
                                        <div className="security-status-item">
                                            <FiShield className="status-icon info" />
                                            <div>
                                                <strong>Autenticacion de dos factores</strong>
                                                <p>Proximamente disponible</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* TAB: NOTIFICACIONES */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        {activeTab === 'notificaciones' && (
                            <div className="urbanq-profile-tab-content">
                                <div className="urbanq-profile-section">
                                    <h2><FiBell /> Preferencias de Notificacion</h2>
                                    <p className="urbanq-profile-section-desc">
                                        Elige que notificaciones deseas recibir
                                    </p>

                                    <div className="urbanq-profile-notifications-list">
                                        <NotificationToggle
                                            icon={<FiCreditCard />}
                                            title="Pagos pendientes"
                                            description="Recibe alertas sobre pagos proximos a vencer"
                                            checked={notifPayments}
                                            onChange={() => setNotifPayments(!notifPayments)}
                                            color="green"
                                        />
                                        <NotificationToggle
                                            icon={<FiBell />}
                                            title="Avisos comunitarios"
                                            description="Recibe notificaciones sobre anuncios de la comunidad"
                                            checked={notifNotices}
                                            onChange={() => setNotifNotices(!notifNotices)}
                                            color="blue"
                                        />
                                        <NotificationToggle
                                            icon={<FiMail />}
                                            title="Correo electronico"
                                            description="Recibe un resumen semanal por correo electronico"
                                            checked={notifEmail}
                                            onChange={() => setNotifEmail(!notifEmail)}
                                            color="purple"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal: Editar Perfil */}
            {showEditModal && (
                <div className="urbanq-profile-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="urbanq-profile-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="urbanq-profile-modal-header">
                            <h3><FiEdit2 /> Editar Perfil</h3>
                            <button className="urbanq-profile-modal-close" onClick={() => setShowEditModal(false)}>
                                <FiX />
                            </button>
                        </div>
                        <div className="urbanq-profile-modal-body">
                            <div className="urbanq-profile-form-group">
                                <label>Nombre Completo</label>
                                <input
                                    type="text"
                                    placeholder="Tu nombre completo"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    disabled={loading}
                                />
                            </div>
                            <div className="urbanq-profile-form-group">
                                <label>Telefono</label>
                                <input
                                    type="tel"
                                    placeholder="+593 999 999 999"
                                    value={editPhone}
                                    onChange={(e) => setEditPhone(e.target.value)}
                                    disabled={loading}
                                />
                            </div>
                            <div className="urbanq-profile-form-group">
                                <label>Correo Electronico</label>
                                <input
                                    type="email"
                                    value={user?.email || ''}
                                    disabled
                                    className="disabled"
                                />
                                <span className="urbanq-profile-form-hint">El correo no puede ser modificado</span>
                            </div>
                        </div>
                        <div className="urbanq-profile-modal-footer">
                            <button
                                className="urbanq-profile-btn secondary"
                                onClick={() => setShowEditModal(false)}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="urbanq-profile-btn primary"
                                onClick={handleUpdateProfile}
                                disabled={loading}
                            >
                                {loading ? (
                                    <><FiRefreshCw className="spinning" /> Guardando...</>
                                ) : (
                                    <><FiCheck /> Guardar Cambios</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════

function InfoRow({ icon, label, value }) {
    return (
        <div className="urbanq-profile-info-row">
            <div className="info-row-icon">{icon}</div>
            <div className="info-row-content">
                <span className="info-row-label">{label}</span>
                <span className="info-row-value">{value}</span>
            </div>
        </div>
    );
}

function ActivityCard({ icon, label, value, color }) {
    return (
        <div className={`urbanq-profile-activity-card ${color}`}>
            <div className="activity-card-icon">{icon}</div>
            <div className="activity-card-info">
                <span className="activity-card-value">{value}</span>
                <span className="activity-card-label">{label}</span>
            </div>
        </div>
    );
}

function NotificationToggle({ icon, title, description, checked, onChange, color }) {
    return (
        <div className="urbanq-profile-notification-item">
            <div className="notification-item-info">
                <div className={`notification-item-icon ${color}`}>{icon}</div>
                <div>
                    <h4>{title}</h4>
                    <p>{description}</p>
                </div>
            </div>
            <label className="urbanq-profile-switch">
                <input type="checkbox" checked={checked} onChange={onChange} />
                <span className="slider"></span>
            </label>
        </div>
    );
}

export default UserProfile;
